import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'
import useOffer from 'hooks/Offers/useOffer'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { isBedbankOffer, isFlashHotelOffer, isLEHotel } from 'lib/offer/offerTypes'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import { LesaNlsBedbankOfferPriceDetails } from './LesaNlsBedbankOfferPriceDetails'
import { LesaNlsLEOfferPriceDetails } from './LesaNlsLEOfferPriceDetails'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { getFirstMeaningfulNlsDescriptionLine } from 'supportAssistant/utils/nlsDescription'
import {
  CardBody,
  CardMetaRow,
  CardMetaText,
  InlineBestMatchPill,
  LesaCarouselCardMedium,
  NlsCardContainer,
  NlsErrorCard,
  NlsMatchHeader,
} from './LesaNlsPropertyCarouselCard.ui'
import { useNlsOfferSelection } from './useNlsOfferSelection'

interface Props {
  compact?: boolean
  property: App.NlsPropertyMatch
  index: number
  nlsOfferParams?: string
  useInlineBestMatchTag?: boolean
  nlsCarouselVariation?: string
}

export default function LesaNlsPropertyCarouselCard({
  compact = false,
  property,
  index,
  nlsOfferParams,
  useInlineBestMatchTag = false,
  nlsCarouselVariation,
}: Props) {
  const intl = useIntl()

  const [offer, loading, error] = useOffer<App.AnyOffer>(property.offerId)

  const hasError = !!error && !loading

  const offerUrl = useMemo(() => (
    `/offer/${property.offerId}${nlsOfferParams ? `?${nlsOfferParams}` : ''}`
  ), [nlsOfferParams, property.offerId])

  const propertyName = useMemo(() => {
    if (!offer) {
      return intl.formatMessage({
        id: 'ZfUCncK6UvoqAGYEesBk',
        defaultMessage: 'this hotel',
        description: 'Fallback name for a hotel property when the actual name is not available',
      })
    }

    if (isLEHotel(offer) && offer.property?.name) {
      return offer.property.name
    }

    if (isBedbankOffer(offer) && offer.property?.name) {
      return offer.property.name
    }

    return offer.name
  }, [intl, offer])

  const location = useMemo(() => {
    if (!offer) {
      return ''
    }

    if (isLEHotel(offer)) {
      return [offer.locationHeading, offer.locationSubheading].filter(Boolean).join(', ')
    }

    if (isBedbankOffer(offer)) {
      return [
        offer.property.address.city,
        offer.property.address.countryName,
      ].filter(Boolean).join(', ')
    }

    return ''
  }, [offer])

  const isInteractive = !loading && !hasError && !!offer

  const bestMatchLabel = intl.formatMessage({
    id: '0Rd69Piwgin6GV6BuMjd',
    defaultMessage: 'Best match',
    description: 'Section heading for the top AI-recommended offer match',
  })

  const matchNumberLabel = intl.formatMessage({
    id: 'WRag4IPFMivpniIxSOek',
    defaultMessage: 'Match #{displayPosition}',
    description: 'Heading for an AI-recommended offer match showing its rank position',
  }, { displayPosition: index + 1 })

  const bestMatchHeading = index === 0 ? `${bestMatchLabel} #${index + 1}` : matchNumberLabel
  const fallbackMatchReasonText = useMemo(() => {
    const rawLabel = property.matchLabel || ''
    const cleanedLabel = rawLabel.replace(/^((Best match|Match #\d+)(\s*#\d+)?)\s*/i, '').trim()
    return cleanedLabel
  }, [property.matchLabel])
  const matchReasonText = useMemo(
    () => getFirstMeaningfulNlsDescriptionLine(property.description) ?? fallbackMatchReasonText,
    [fallbackMatchReasonText, property.description],
  )
  const inlineBestMatchTag = useMemo(
    () => <InlineBestMatchPill compact={compact} heading={bestMatchHeading} />,
    [bestMatchHeading, compact],
  )

  const bookmarkButton = useMemo(() => {
    if (!offer) {
      return undefined
    }
    return <BookmarkButton offer={offer} withLabel="always" size={compact ? 'small' : undefined} />
  }, [compact, offer])

  const { handleSelect } = useNlsOfferSelection({
    property,
    index,
    propertyName,
    location,
    isInteractive,
    hasOffer: !!offer,
    nlsOfferParams,
    nlsCarouselVariation,
  })

  const leOfferCard = useMemo(() => {
    if (!offer || !isLEHotel(offer)) {
      return null
    }

    const isFlashHotel = isFlashHotelOffer(offer)
    const isCharterBundleOffer = isFlashHotel &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

    const charterBundleImageLabel = isCharterBundleOffer ? (
      <StructureLabel variant="maldives-charter">
        {intl.formatMessage({
          id: 'PjmdhR5JAQXM7v4WOevW',
          defaultMessage: 'Hotel & Flight bundle',
          description: 'Label for charter bundle offers combining hotel and flight',
        })}
      </StructureLabel>
    ) : undefined

    const charterBundleRegulatoryNotice = isCharterBundleOffer ? (
      <Caption variant="medium" italics colour="neutral-two">
        {intl.formatMessage({
          id: 'e4HqVb97x-L75qcGV89M',
          defaultMessage: 'Subject to Government & Regulatory Approvals',
          description: 'Government and regulatory approval notice for charter bundle offers',
        })}
      </Caption>
    ) : undefined

    const cardLocation = (
      <CardMetaRow>
        <CardMetaText>
          <BodyText colour="neutral-one" variant={compact ? 'small' : 'medium'} lineClamp={1}>{location}</BodyText>
          <BodyText colour="neutral-one" variant={compact ? 'small' : 'medium'} weight="bold" lineClamp={1}>{offer.property.name}</BodyText>
        </CardMetaText>
        {useInlineBestMatchTag && inlineBestMatchTag}
      </CardMetaRow>
    )

    const productLabel = isFlashHotel && !isCharterBundleOffer ? <LesaCardProductLabel offer={offer} /> : undefined

    const commonCardProps = {
      'data-testid': 'lesa-nls-offer-card' as const,
      bookmarkButton,
      image: offer.image,
      images: offer.images,
      location: cardLocation,
      title: offer.name,
      titleVariant: compact ? 'medium' as const : 'large' as const,
      to: offerUrl,
      rating: !useInlineBestMatchTag && isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined,
      onClick: handleSelect,
    }

    return (
      <ProductPaletteProvider product={offer}>
        <LesaCarouselCardMedium
          {...commonCardProps}
          $compact={compact}
          priceDetails={<LesaNlsLEOfferPriceDetails compact={compact} offer={offer} />}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          charterBundleLabels={charterBundleRegulatoryNotice}
          productLabel={productLabel}
        />
      </ProductPaletteProvider>
    )
  }, [bookmarkButton, compact, handleSelect, inlineBestMatchTag, intl, location, offer, offerUrl, useInlineBestMatchTag])

  const bedbankOfferCard = useMemo(() => {
    if (!offer || !isBedbankOffer(offer)) {
      return null
    }

    const providerName = offer.property.name !== offer.name ? offer.property.name : undefined
    const cardLocation = (
      <CardMetaRow>
        <CardMetaText>
          <BodyText colour="neutral-one" variant={compact ? 'small' : 'medium'} lineClamp={1}>{location}</BodyText>
          {!!providerName && <BodyText colour="neutral-one" variant={compact ? 'small' : 'medium'} weight="bold" lineClamp={1}>{providerName}</BodyText>}
        </CardMetaText>
        {useInlineBestMatchTag && inlineBestMatchTag}
      </CardMetaRow>
    )

    const commonCardProps = {
      'data-testid': 'lesa-nls-offer-card' as const,
      bookmarkButton,
      image: offer.image,
      images: offer.images,
      location: cardLocation,
      title: offer.name,
      titleVariant: compact ? 'medium' as const : 'large' as const,
      priceDetails: <LesaNlsBedbankOfferPriceDetails compact={compact} offer={offer} />,
      to: offerUrl,
      rating: !useInlineBestMatchTag && isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined,
      onClick: handleSelect,
    }

    return (
      <ProductPaletteProvider palette="default">
        <LesaCarouselCardMedium {...commonCardProps} $compact={compact} />
      </ProductPaletteProvider>
    )
  }, [bookmarkButton, compact, handleSelect, inlineBestMatchTag, location, offer, offerUrl, useInlineBestMatchTag])

  const errorMessage = intl.formatMessage({
    id: 'nY4i_AhUhgb8dH-jOFyH',
    defaultMessage: 'Unable to load offer details',
    description: 'Error message when hotel offer details fail to load',
  })

  return (
    <NlsCardContainer useInlineBestMatchTag={useInlineBestMatchTag}>
      {!useInlineBestMatchTag && (
        <NlsMatchHeader compact={compact} heading={bestMatchHeading} reasonText={matchReasonText} />
      )}
      <CardBody>
        {loading && <OfferCardLoadingSkeleton size="medium" variant="card-default" />}
        {hasError && <NlsErrorCard compact={compact} message={errorMessage} />}
        {!loading && !hasError && leOfferCard}
        {!loading && !hasError && !leOfferCard && bedbankOfferCard}
      </CardBody>
    </NlsCardContainer>
  )
}

function LesaCardProductLabel({ offer }: { offer: App.HotelOffer | App.HotelOfferSummary }) {
  const { isAnyVariant: isLtleHomepageEnabled } = useExperiment(Experiments.croDisplayLtleOnHomepage, {
    canActivate: isFlashHotelOffer(offer),
  })

  if (!isLtleHomepageEnabled) {
    return undefined
  }

  return (
    <LtleTooltipWrapper variant="light">
      <ProductTypeLabel productType={offer.productType} />
    </LtleTooltipWrapper>
  )
}
